import { z } from 'zod';
import { useTranslation } from 'react-i18next';
import { useFormHook } from '@Hooks/index';
import { EddEmbeddedFields } from '@Pages/happyFlowPages/eddQuestions/pages/embedded/fields.types';
import {
  getDefaultValueByQuestionId,
  saveEddQuestionToLocalStore,
} from '@Pages/happyFlowPages/eddQuestions/shared.utils';
import { EDD_VERSION } from '@Pages/happyFlowPages/eddQuestions/consts';
import { useBoundStore } from '@Store/useBoundStore';
import { useNavigationManager } from '@Router/useNavigationManager';
import { usePostEddData } from '@Pages/happyFlowPages/eddQuestions/hooks/usePostEddData';
import { RoutesMap } from '@Router/router.types.ts';
import { OrganizationRoleOptions } from '../OrganizationRoleForm';

export const useOrganizationRoleLogic = () => {
  const { mutateAsync: postEddData, isPending: isPostingEddData } = usePostEddData();
  const { getEddEmbeddedQuestions } = useBoundStore((state) => state);
  const { navigateToNextPage } = useNavigationManager();
  const { t } = useTranslation('eddQuestions');
  const { t: tValidation } = useTranslation('enJSON');

  const formMethods = useFormHook({
    schemaTitle: 'Organization Role',
    formDefaultValues: {
      [EddEmbeddedFields.EDD_ORGANIZATION_ROLE]:
        getDefaultValueByQuestionId(EddEmbeddedFields.EDD_ORGANIZATION_ROLE) || '',
      [EddEmbeddedFields.EDD_ORGANIZATION_ROLE_OTHER_FREE_TEXT]:
        getDefaultValueByQuestionId(EddEmbeddedFields.EDD_ORGANIZATION_ROLE_OTHER_FREE_TEXT) || '',
    },
    formSchema: z
      .object({
        [EddEmbeddedFields.EDD_ORGANIZATION_ROLE]: z
          .string()
          .min(1, { message: tValidation('validation.required') }),
        [EddEmbeddedFields.EDD_ORGANIZATION_ROLE_OTHER_FREE_TEXT]: z.string().optional(),
      })
      .superRefine((data, ctx) => {
        const isOtherSelected =
          data[EddEmbeddedFields.EDD_ORGANIZATION_ROLE] === OrganizationRoleOptions.OTHER;

        if (isOtherSelected) {
          const freeText = data[EddEmbeddedFields.EDD_ORGANIZATION_ROLE_OTHER_FREE_TEXT];

          if (!freeText || freeText.length === 0) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: tValidation('validation.required'),
              path: [EddEmbeddedFields.EDD_ORGANIZATION_ROLE_OTHER_FREE_TEXT],
            });
          } else if (freeText.length < 30) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: tValidation('validation.min30chars'),
              path: [EddEmbeddedFields.EDD_ORGANIZATION_ROLE_OTHER_FREE_TEXT],
            });
          } else if (freeText.length > 100) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: tValidation('validation.max100chars'),
              path: [EddEmbeddedFields.EDD_ORGANIZATION_ROLE_OTHER_FREE_TEXT],
            });
          }
        }
      }),
  });

  const handleSubmit = async () => {
    saveEddQuestionToLocalStore({
      question: t('organic.organizationRole.title'),
      questionId: EddEmbeddedFields.EDD_ORGANIZATION_ROLE,
      answer: JSON.stringify(formMethods.getValues(EddEmbeddedFields.EDD_ORGANIZATION_ROLE)),
    });

    saveEddQuestionToLocalStore({
      question: `${t('organic.organizationRole.title')} - other free text`,
      questionId: EddEmbeddedFields.EDD_ORGANIZATION_ROLE_OTHER_FREE_TEXT,
      answer: JSON.stringify(
        formMethods.getValues(EddEmbeddedFields.EDD_ORGANIZATION_ROLE_OTHER_FREE_TEXT)
      ),
    });

    try {
      await postEddData({
        version: EDD_VERSION,
        results: getEddEmbeddedQuestions(),
      });
      navigateToNextPage(RoutesMap.EDD_ORGANIZATION_ROLE);
    } catch (error) {
      console.error(error);
    }
  };

  return {
    formMethods,
    handleSubmit,
    isLoading: isPostingEddData,
  };
};
